import {
  ArrowLeft,
  MoreVertical,
  Send,
  Flag,
  Wifi,
  WifiOff,
  RefreshCw,
} from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Capacitor } from "@capacitor/core";
import ChatBubble from "../../../../components/ChatBubble/ChatBubble";
import { PhotoImage } from "../../../../components/common/PhotoImage";
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import { Admin_GetMessageHistory } from "../../../../constants/adminData";
import { apiVariables } from "../../../../services/api/apiVariables";
import { getToken } from "../../../../utils/tokenUtils";
import { getSensitiveData } from "../../../../utils/secureStorage";
import ReportModal from "../../../../components/common/ReportModal";
import Toast from "../../../../components/Toast/Toast";
// import AdMob from "../../../../components/common/AdMob";
// import { BannerAdSize } from "@capacitor-community/admob";
// import config from "../../../../config/config";
import "./MessageChat.css";
import ReportconfirmDialog from "../../../../components/ConfirmDialog/ReportconfirmDialog";
import { useTheme } from "../../../../context/ThemeContext";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Message {
  id: number;
  text: string;
  time: string;
  sentAt: string;
  isSent: boolean;
  deliveryStatus?: number;
  showAvatar?: boolean;
}

interface MessageChatProps {
  userId: string;
  userName: string;
  onBack: () => void;
  userPhotoId?: string;
  scrollToMessageId?: number;
}

type ConnectionStatus = "connected" | "reconnecting" | "disconnected";

// ─── Component ────────────────────────────────────────────────────────────────

export default function MessageChat({
  userId,
  userName,
  onBack,
  scrollToMessageId,
  userPhotoId,
}: MessageChatProps) {
  const { theme } = useTheme();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("disconnected");
  const [isOnline, setIsOnline] = useState(false);

  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  // ── Refs ──────────────────────────────────────────────────────────────────

  const connectionRef = useRef<HubConnection | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledToTargetRef = useRef(false);
  // Use negative-integer temp IDs so they never collide with real server IDs
  const tempIdCounterRef = useRef<number>(0);
  // Keep conversationId accessible inside SignalR callbacks without stale closure
  const conversationIdRef = useRef<number | null>(null);

  const [authToken, setAuthToken] = useState<string>("");
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [showConnectedBanner, setShowConnectedBanner] = useState(false);

  useEffect(() => {
    Promise.all([
      getToken(),
      getSensitiveData("userId"),
    ]).then(([token, uid]) => {
      setAuthToken(token ?? "");
      setCurrentUserId(uid ?? "");
    });
  }, []);
  const planId = useSelector((state: any) => state.userProfile?.planId ?? 0);
  const fullCapabilities = useSelector((state: any) => state.userProfile?.fullCapabilities ?? false);

  const [confirmBox, setConfirmBox] = useState<{
    message: string;
    resolve: (value: boolean) => void;
    singleButton?: boolean;
  } | null>(null);

    // Keep ref in sync with state so callbacks always have the latest value
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const showConfirm = (message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmBox({ message, resolve });
    });
  };

  const safeJoinConversation = useCallback(
    (conn: HubConnection, convId: number | null) => {
      if (!convId) return;
      if (conn.state !== HubConnectionState.Connected) return;
      conn
        .invoke("JoinConversation", convId)
        .catch((err) => console.error("Failed to join conversation:", err));
    },
    []
  );

  // ── Scroll to bottom or target message ─────────────────────────────────────

  // Reset scroll-to-target flag when the target message changes
  useEffect(() => {
    hasScrolledToTargetRef.current = false;
  }, [scrollToMessageId]);

  useEffect(() => {
    if (!messages.length) return;

    if (scrollToMessageId && !hasScrolledToTargetRef.current) {
      const target = messageListRef.current?.querySelector(
        `[data-message-id="${scrollToMessageId}"]`
      ) as HTMLElement | null;

      if (target) {
        hasScrolledToTargetRef.current = true;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.style.transition = 'background-color 0.4s ease';
        target.style.backgroundColor = '#fef9c3';
        setTimeout(() => {
          target.style.backgroundColor = '';
        }, 2500);
      }
    } else if (!scrollToMessageId) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, scrollToMessageId]);

  // ── Load message history ──────────────────────────────────────────────────

  useEffect(() => {
    if (!authToken || !userId) return;

    const load = async () => {
      const res = await Admin_GetMessageHistory(userId, authToken);
      if (!res?.success) {
        console.error("❌ Failed to load message history:", res);
        return;
      }

      setConversationId(Number(res.data.conversationId));

      setMessages(
        res.data.messages.map((m: any) => {
          const isSent =
            m.senderId.toLowerCase() === currentUserId.toLowerCase();
          return {
            id: m.messageId,
            text: m.messageContent,
            time: formatTime(m.sentAt),
            sentAt: m.sentAt,
            isSent,
            deliveryStatus: m.deliveryStatus ?? 1,
            showAvatar: !isSent,
          };
        })
      );
    };

    load();
  }, [userId, authToken, currentUserId]);

  // ── Mark messages as read when they arrive / connection is ready ──────────

  useEffect(() => {
    const conn = connectionRef.current;
    if (
      !conn ||
      conn.state !== HubConnectionState.Connected ||
      !conversationId
    )
      return;

    const unreadIds = messages
      .filter((m) => !m.isSent && m.deliveryStatus !== 2)
      .map((m) => m.id);

    if (unreadIds.length > 0) {
      conn
        .invoke("MarkMessagesAsRead", conversationId, unreadIds)
        .catch((err) =>
          console.error("❌ Failed to mark messages as read:", err)
        );
    }
  }, [messages, conversationId, connectionStatus]);

  // ── SignalR connection ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!authToken || !currentUserId) return;

    let isMounted = true;

    const createConnection = async () => {
    
      const token = authToken || (await getToken());
      if (!token) {
        throw new Error('No auth token available to establish chat connection.');
      }

      const connection = new HubConnectionBuilder()
        .withUrl(apiVariables.CHAT_HUB_URL, {
          accessTokenFactory: () => token,
          skipNegotiation: Capacitor.isNativePlatform(),
          withCredentials: true,
          transport: Capacitor.isNativePlatform()
            ? HttpTransportType.WebSockets
            : HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(LogLevel.Information)
        .build();

      // ── Lifecycle handlers ──────────────────────────────────────────────

      connection.onreconnecting(() => {
        if (isMounted) setConnectionStatus("reconnecting");
      });

      connection.onreconnected(() => {
        if (!isMounted) return;
        setConnectionStatus("connected");
        connection.invoke("JoinUserGroup", currentUserId).catch((err) =>
          console.error("Failed to rejoin user broadcast group:", err)
        );
        // Re-join conversation after reconnect using ref (avoids stale closure)
        safeJoinConversation(connection, conversationIdRef.current);
      });

      connection.onclose(() => {
        if (isMounted) setConnectionStatus("disconnected");
      });

      // ── Message handlers ────────────────────────────────────────────────

      connection.on("ReceiveMessage", (msg: any) => {
        if (!msg?.messageId) return;
        if (msg.conversationId !== conversationIdRef.current)return;
        
        const isSent =
          msg.senderId?.toLowerCase() === currentUserId.toLowerCase();

        if (!isSent) setIsOnline(true);

        setMessages((prev) => {
          // Deduplicate real messages
          if (prev.find((m) => m.id === msg.messageId)) return prev;

          if (isSent) {
            // Replace optimistic (temp) message with confirmed one
            const tempIdx = prev.findIndex(
              (m) =>
                m.text === msg.messageContent &&
                m.isSent &&
                m.id < 0 // temp IDs are negative
            );

            if (tempIdx !== -1) {
              const updated = [...prev];
              updated[tempIdx] = {
                ...prev[tempIdx],
                id: msg.messageId,
                deliveryStatus: msg.deliveryStatus ?? 1,
                time: formatTime(msg.sentAt),
              };
              return updated;
            }
            return prev;
          }

          // Incoming message from the other user
          const newMessage: Message = {
            id: msg.messageId,
            text: msg.messageContent,
            time: formatTime(msg.sentAt),
            sentAt: msg.sentAt,
            isSent: false,
            deliveryStatus: msg.deliveryStatus ?? 1,
            showAvatar: true,
          };

          // Mark as read after a short delay
          setTimeout(() => {
            if (
              connection.state === HubConnectionState.Connected &&
              msg.conversationId
            ) {
              connection
                .invoke("MarkMessagesAsRead", msg.conversationId, [
                  msg.messageId,
                ])
                .catch((err) =>
                  console.error("Failed to mark new message as read:", err)
                );
            }
          }, 500);

          return [...prev, newMessage];
        });
      });

      connection.on("MessagesRead", (payload: any) => {
        const messageIds =
          payload?.MessageIds || payload?.messageIds || payload;
        if (!Array.isArray(messageIds)) return;

        setIsOnline(true);

        setMessages((prev) =>
          prev.map((m) =>
            messageIds.includes(m.id) && m.deliveryStatus !== 2
              ? { ...m, deliveryStatus: 2 }
              : m
          )
        );
      });

      connection.on("UserOnline", (payload: any) => {
        const targetId = payload?.UserId || payload?.userId || payload;
        if (targetId?.toLowerCase() === userId.toLowerCase()) setIsOnline(true);
      });

      connection.on("UserOffline", (payload: any) => {
        const targetId = payload?.UserId || payload?.userId || payload;
        if (targetId?.toLowerCase() === userId.toLowerCase())
          setIsOnline(false);
      });

      // ── Start ───────────────────────────────────────────────────────────

      try {
        await connection.start();

        if (!isMounted) {
          await connection.stop();
          return;
        }

        // Null out the old ref immediately before assigning new one
        connectionRef.current = null;
        connectionRef.current = connection;
        setConnectionStatus("connected");

        await connection.invoke("JoinUserGroup", currentUserId);
        safeJoinConversation(connection, conversationIdRef.current);
      } catch (err: any) {
        if (isMounted) {
          console.error("SignalR connection failed:", err?.message);
          setConnectionStatus("disconnected");
        }
      }
    };

    createConnection();

    return () => {
      isMounted = false;
      const c = connectionRef.current;
      connectionRef.current = null; // null immediately to prevent stale use
      setConnectionStatus("disconnected");
      c?.stop().catch((err) => console.error("Error stopping SignalR:", err));
    };
  }, [authToken, currentUserId, userId, safeJoinConversation]);

  // ── Join conversation when conversationId resolves after connection ────────

  useEffect(() => {
    const conn = connectionRef.current;
    if (conn && conn.state === HubConnectionState.Connected && conversationId) {
      safeJoinConversation(conn, conversationId);
    }
  }, [conversationId, connectionStatus, safeJoinConversation]);

  // ── Send message ──────────────────────────────────────────────────────────

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const conn = connectionRef.current;

    if (!conn || conn.state !== HubConnectionState.Connected) {
      setToast({
        message: "Connection lost. Please wait while we reconnect…",
        type: "error",
      });
      return;
    }

    const content = inputMessage.trim();
    setInputMessage("");

    // Negative temp ID — guaranteed not to clash with positive server IDs
    const tempId = --tempIdCounterRef.current;
    const now = new Date().toISOString();

    const tempMessage: Message = {
      id: tempId,
      text: content,
      time: formatTime(now),
      sentAt: now,
      isSent: true,
      deliveryStatus: 0,
    };

    setMessages((prev) => [...prev, tempMessage]);

    try {
      await conn.invoke("SendMessage", {
        receiverId: userId,
        conversationId,
        messageContent: content,
      });
    } catch (err) {
      console.error("Failed to send message:", err);
      // Remove the optimistic message on failure
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setToast({ message: "Failed to send message. Please try again.", type: "error" });
    }
  };

  // ── Report handlers ───────────────────────────────────────────────────────

  const handleReportChat = () => {
    setShowMenu(false);
    setIsReportModalOpen(true);
  };

  const handleSubmitReport = async (data: {
    reason: string;
    entityType: number;
    entityId: string;
  }) => {
    try {
      const reportingUserId = (await getSensitiveData("userId")) || "";
      const response = await fetch(apiVariables.REPORT_SUBMIT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getToken()}`,
        },
        body: JSON.stringify({
          reportingUserId,
          reportedUserId: userId,
          reportedEntityType: data.entityType,
          reportedEntityId: data.entityId,
          reportReason: data.reason,
        }),
      });

      const result = await response.json().catch(() => null);

      if (response.ok && result?.success) {
        setToast({
          message: result?.message || "Chat reported successfully.",
          type: "success",
        });
        setIsReportModalOpen(false);
      } else {
        const errorMessage =
          result?.message ||
          (Array.isArray(result?.errors) && result.errors.length > 0
            ? result.errors[0]
            : "Failed to submit report.");

        setConfirmBox({
          message: errorMessage,
          resolve: () => {},
          singleButton: true,
        });
      }
    } catch (error: any) {
      setConfirmBox({
        message: error?.message || "An error occurred. Please try again.",
        resolve: () => {},
      });
    }
  };

  // ── Connection status bar config ──────────────────────────────────────────

  const statusConfig: Record<
    ConnectionStatus,
    { label: string; bg: string; text: string; icon: React.ReactNode; show: boolean }
  > = {
    connected: {
      label: "Connected",
      bg: "bg-emerald-50 border-emerald-200",
      text: "text-emerald-700",
      icon: <Wifi className="w-3.5 h-3.5" />,
      show: false,
    },
    reconnecting: {
      label: "Reconnecting…",
      bg: "bg-amber-50 border-amber-200",
      text: "text-amber-700",
      icon: (
        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
      ),
      show: false,
    },
    disconnected: {  
      label: "Disconnected — messages cannot be sent",
      bg: "bg-red-50 border-red-200",
      text: "text-red-700",
      icon: <WifiOff className="w-3.5 h-3.5" />,
      show: false,
    },
  };

  const status = statusConfig[connectionStatus];
  const isCanSend = connectionStatus === "connected";

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="h-full min-h-0 bg-[#E8E8E8] flex flex-col overflow-hidden">
      {/* ── Header ── */}
      <div className="px-6 py-4 flex justify-between items-center flex-shrink-0 bg-white shadow-sm">
        <button
          onClick={onBack}
          className="min-w-[44px] min-h-[44px] flex items-center justify-center"
        >
          <ArrowLeft className="text-brand-primary w-6 h-6" />
        </button>

        <div className="flex items-center gap-3 flex-1 justify-center">
          <PhotoImage
            photoId={userPhotoId}
            alt={userName}
            className="w-10 h-10 rounded-full object-cover"
            fallback={
              <div className="w-10 h-10 rounded-full bg-gradient-to-b from-brand-primary to-brand-gray flex items-center justify-center text-white text-[14px] font-[600]">
                {getInitials(userName)}
              </div>
            }
          />
          <div className="text-center">
            <div className="font-bold text-[18px] text-[#212121] truncate max-w-[200px]">
              {userName}
            </div>
            <div className="text-xs flex gap-2 justify-center items-center mt-1">
              <span
                className={`w-2 h-2 rounded-full ${
                  isOnline ? "bg-green-500" : "bg-gray-400"
                }`}
              />
              <span className="text-[#757575]">
                {isOnline ? "Online" : "Offline"}
              </span>
            </div>
          </div>
        </div>

        <div className="relative">
          <button onClick={() => setShowMenu(!showMenu)}>
            <MoreVertical className="text-brand-primary" />
          </button>

          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMenu(false)}
              />
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                <button
                  onClick={handleReportChat}
                  disabled={messages.length === 0}
                  className={`w-full px-4 py-3 text-left flex items-center gap-2 rounded-lg ${
                    messages.length === 0
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-50 text-gray-700"
                  }`}
                >
                  <Flag
                    className={`w-4 h-4 ${
                      messages.length === 0 ? "text-gray-400" : "text-brand-primary"
                    }`}
                  />
                  Report Chat
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Message list ── */}
      <div ref={messageListRef} className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
        {messages.map((m, index) => {
          const prev = messages[index - 1];
          const showDateHeader =
            !prev || getDateLabel(prev.sentAt) !== getDateLabel(m.sentAt);

          return (
            <div key={m.id} data-message-id={m.id}>
              {showDateHeader && (
                <div className="flex justify-center my-3">
                  <span className="px-3 py-1 text-xs text-gray-600 bg-gray-200 rounded-full">
                    {getDateLabel(m.sentAt)}
                  </span>
                </div>
              )}
              <ChatBubble
                message={m.text}
                time={m.time}
                isSent={m.isSent}
                deliveryStatus={m.deliveryStatus}
                showAvatar={m.showAvatar}
                userPhotoId={userPhotoId}
                userName={userName}
              />
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* ── Connection status bar (shown only when not connected) ── */}
      {status.show && (
        <div
          className={`mx-4 mb-2 px-3 py-2 rounded-lg border flex items-center gap-2 flex-shrink-0 transition-all duration-300 ${status.bg}`}
        >
          <span className={status.text}>{status.icon}</span>
          <span className={`text-xs font-medium ${status.text}`}>
            {status.label}
          </span>
        </div>
      )}

      {/* ── Input area ── */}
      {planId === 1 && !fullCapabilities ? (
        <div className="px-6 py-4 flex gap-3 flex-shrink-0 bg-white border-t border-gray-200">
          <button
            disabled
            title="Subscription required"
            className="bg-brand-primary w-full py-3 rounded-md text-white font-semibold opacity-50 cursor-not-allowed"
          >
            Subscribe to send message
          </button>
        </div>
      ) : (
        <form
          onSubmit={sendMessage}
          className="px-6 py-4 flex gap-3 flex-shrink-0 bg-white border-t border-gray-200"
        >
          <input
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder={
              isCanSend ? "Type a message…" : "Waiting for connection…"
            }
            disabled={!isCanSend}
            className="flex-1 rounded-full px-4 py-2 border focus:outline-none focus:border-brand-primary transition-colors placeholder:text-slate-400 disabled:cursor-not-allowed"
            style={{
              backgroundColor: isCanSend ? theme.surface : theme.surfaceHover,
              borderColor: theme.border,
              color: isCanSend ? theme.text : theme.textDisabled,
            }}
          />
          <button
            disabled={!isCanSend}
            className="bg-brand-primary w-12 h-12 rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-hover transition-colors"
            type="submit"
          >
            <Send className="text-white w-5 h-5" />
          </button>
        </form>
      )}

      {/* <AdMob
        adId={config.ADMOB_AD_UNITS.messageWindow}
        adSize={BannerAdSize.LARGE_BANNER}
        placement="message-window"
        inline={true}
      /> */}

      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        onSubmit={handleSubmitReport}
        reportType="chat"
        userName={userName}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {confirmBox && (
        <ReportconfirmDialog
          message={confirmBox.message}
          singleButton={confirmBox.singleButton}
          onCancel={() => {
            confirmBox.resolve(false);
            setConfirmBox(null);
          }}
          onConfirm={() => {
            confirmBox.resolve(true);
            setConfirmBox(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function formatTime(dateString: string) {
  if (!dateString) return "";
  const normalized = /Z$|[+-]\d{2}:\d{2}$/.test(dateString)
    ? dateString
    : dateString + "Z";
  return new Date(normalized).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

function getDateLabel(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (d1: Date, d2: Date) =>
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear();

  if (isSameDay(date, today)) return "Today";
  if (isSameDay(date, yesterday)) return "Yesterday";

  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
